// src/pages/recap/Recap.js
import React, { useEffect, useState } from 'react';
import './recap.css';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faFire,
  faTint,
  faEgg,
  faBreadSlice,
  faCheese,
  faWeightScale,
  faTimes,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';

const API_BASE = process.env.REACT_APP_API_BASE;

const nomesMeses = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril',
  'Maio', 'Junho', 'Julho', 'Agosto',
  'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const hojeGlobal = new Date();

// helper para formatar data YYYY-MM-DD -> dd/mm/yyyy
function formatarDataBr(iso) {
  if (!iso) return '';
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}

// monta matriz de semanas para exibir o calendário em grade (Seg → Dom)
function montarMatrizDias(ano, mes) {
  // mes vem como 1..12, JS usa 0..11
  const firstDay = new Date(ano, mes - 1, 1);
  const lastDay = new Date(ano, mes, 0); // dia 0 do próximo mês = último do mês atual

  const totalDias = lastDay.getDate();

  // getDay(): 0 = Domingo, 1 = Segunda, ... 6 = Sábado
  // Queremos que a coluna 0 seja Segunda, então ajustamos:
  // seg(1) -> 0, ter(2) -> 1, ..., dom(0) -> 6
  const firstWeekdayJs = firstDay.getDay();
  const startIndex = (firstWeekdayJs + 6) % 7;

  const semanas = [];
  let semanaAtual = new Array(7).fill(null);
  let dia = 1;

  // preenche os "buracos" antes do dia 1
  for (let i = 0; i < startIndex; i++) {
    semanaAtual[i] = null;
  }

  // preenche dias
  for (let i = startIndex; i < 7; i++) {
    semanaAtual[i] = dia;
    dia++;
  }
  semanas.push(semanaAtual);

  while (dia <= totalDias) {
    const novaSemana = new Array(7).fill(null);
    for (let i = 0; i < 7 && dia <= totalDias; i++) {
      novaSemana[i] = dia;
      dia++;
    }
    semanas.push(novaSemana);
  }

  return semanas;
}

const Recap = () => {
  const navigate = useNavigate();

  const hoje = hojeGlobal;
  const anoAtual = hoje.getFullYear();
  const mesAtual = hoje.getMonth() + 1;
  const diaAtual = hoje.getDate();

  const [ano, setAno] = useState(anoAtual);
  const [mes, setMes] = useState(mesAtual); // 1..12
  const [diasMapa, setDiasMapa] = useState({}); // { '2025-12-03': { calorias, agua, peso, ... } }
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  const [diaSelecionado, setDiaSelecionado] = useState(null); // 'YYYY-MM-DD'
  const [dadosSelecionados, setDadosSelecionados] = useState(null); // objeto com calorias, agua, etc

  const semanas = montarMatrizDias(ano, mes);

  // está no mes/ano atual?
  const isMesAtual = ano === anoAtual && mes === mesAtual;

  const carregarRecap = async (anoParam, mesParam) => {
    setLoading(true);
    setErro(null);

    try {
      const url = `${API_BASE}/recap-mes.php?ano=${anoParam}&mes=${mesParam}`;
      const res = await fetch(url, {
        credentials: 'include'
      });

      if (!res.ok) {
        throw new Error('Erro HTTP ao buscar recap do mês.');
      }

      const data = await res.json();

      if (data.status !== 'sucesso') {
        throw new Error(data.mensagem || 'Erro ao buscar recap.');
      }

      // data.dias é um objeto: { '2025-12-01': {calorias, agua, ...}, ... }
      setDiasMapa(data.dias || {});
    } catch (err) {
      console.error('Erro ao carregar recap:', err);
      setErro(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarRecap(ano, mes);
  }, [ano, mes]);

  const irMesAnterior = () => {
    setDiaSelecionado(null);
    setDadosSelecionados(null);

    let novoMes = mes - 1;
    let novoAno = ano;
    if (novoMes < 1) {
      novoMes = 12;
      novoAno = ano - 1;
    }
    setMes(novoMes);
    setAno(novoAno);
  };

  const irProximoMes = () => {
    // proteção extra: nunca ir além do mês atual
    let novoMes = mes + 1;
    let novoAno = ano;
    if (novoMes > 12) {
      novoMes = 1;
      novoAno = ano + 1;
    }

    // bloqueia se for futuro
    if (novoAno > anoAtual) return;
    if (novoAno === anoAtual && novoMes > mesAtual) return;

    setDiaSelecionado(null);
    setDadosSelecionados(null);
    setMes(novoMes);
    setAno(novoAno);
  };

  const aoClicarDia = (diaNumero) => {
    if (!diaNumero) return;

    const diaStr = String(diaNumero).padStart(2, '0');
    const mesStr = String(mes).padStart(2, '0');
    const dataIso = `${ano}-${mesStr}-${diaStr}`;

    const dados = diasMapa[dataIso] || {
      calorias: 0,
      proteinas: 0,
      carboidratos: 0,
      gorduras: 0,
      agua: 0,
      peso: null
    };

    setDiaSelecionado(dataIso);
    setDadosSelecionados(dados);
  };

  const fecharModal = () => {
    setDiaSelecionado(null);
    setDadosSelecionados(null);
  };

  // data ISO do dia de hoje (para destacar no calendário)
  const dataHojeIso = `${anoAtual}-${String(mesAtual).padStart(2, '0')}-${String(diaAtual).padStart(2, '0')}`;

  return (
    <div className="recap-container">
      {/* TOPO PADRÃO (logo centralizada) */}
<div className="recap-topo-central">
  <img
    src="/assets/logo2.png"
    alt="Logo"
    className="cardapios-logo-central"
  />
</div>


      <header className="recap-header">
        <button className="recap-nav-btn" onClick={irMesAnterior}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <div className="recap-header-title">
          <h2>{nomesMeses[mes - 1]} {ano}</h2>
          <p>Resumo diário de calorias, água, macros e peso</p>
        </div>
        <button
          className={`recap-nav-btn ${isMesAtual ? 'disabled' : ''}`}
          onClick={isMesAtual ? undefined : irProximoMes}
          disabled={isMesAtual}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </header>

      {loading && (
        <div className="recap-loading">Carregando dados do mês...</div>
      )}

      {erro && !loading && (
        <div className="recap-error">
          Ocorreu um erro ao buscar os dados: {erro}
        </div>
      )}

      {!loading && !erro && (
        <>
         

          <div className="recap-calendario">
            <div className="recap-calendario-header-row">
              <div>Seg</div>
              <div>Ter</div>
              <div>Qua</div>
              <div>Qui</div>
              <div>Sex</div>
              <div>Sáb</div>
              <div>Dom</div>
            </div>

            <div className="recap-calendario-body">
              {semanas.map((semana, idxSemana) => (
                <div className="recap-calendario-week" key={`semana-${idxSemana}`}>
                  {semana.map((diaNumero, idxDia) => {
                    if (!diaNumero) {
                      return <div key={`empty-${idxSemana}-${idxDia}`} className="recap-calendario-day empty"></div>;
                    }

                    const diaStr = String(diaNumero).padStart(2, '0');
                    const mesStr = String(mes).padStart(2, '0');
                    const dataIso = `${ano}-${mesStr}-${diaStr}`;
                    const dadosDia = diasMapa[dataIso] || {
                      calorias: 0,
                      proteinas: 0,
                      carboidratos: 0,
                      gorduras: 0,
                      agua: 0,
                      peso: null
                    };

                    const kcal = Math.round(dadosDia.calorias || 0);
                    const aguaMl = dadosDia.agua || 0;
                    const aguaLitros = (aguaMl / 1000).toFixed(1);
                    const peso = dadosDia.peso;

                    const temDados = kcal > 0 || aguaMl > 0 || (peso !== null && peso !== undefined);

                    const isHoje = dataIso === dataHojeIso;
                    const isSelected = dataIso === diaSelecionado;

                    const classNames = [
                      'recap-calendario-day',
                      temDados ? 'has-data' : '',
                      isHoje ? 'today' : '',
                      isSelected ? 'selected' : ''
                    ].join(' ').trim();

                    return (
                      <div
                        key={`dia-${idxSemana}-${idxDia}`}
                        className={classNames}
                        onClick={() => aoClicarDia(diaNumero)}
                      >
                        <div className="recap-day-number">{diaNumero}</div>
                        <div className="recap-day-metrics">
                          <div className="recap-day-kcal">
                            <FontAwesomeIcon icon={faFire} /> {kcal} kcal
                          </div>
                          <div className="recap-day-agua">
                            <FontAwesomeIcon icon={faTint} /> {aguaLitros} L
                          </div>
                          <div className="recap-day-peso">
                            <FontAwesomeIcon icon={faWeightScale} /> {peso ? `${peso.toFixed(1)} kg` : '--'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {diaSelecionado && dadosSelecionados && (
        <div className="recap-modal-overlay" onClick={fecharModal}>
          <div className="recap-modal" onClick={(e) => e.stopPropagation()}>
            <button className="recap-modal-close" onClick={fecharModal}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h3>Detalhes do dia {formatarDataBr(diaSelecionado)}</h3>

            <div className="recap-modal-grid">
              <div className="recap-modal-card">
                <h4><FontAwesomeIcon icon={faFire} /> Calorias</h4>
                <p className="recap-modal-valor-grande">
                  {Math.round(dadosSelecionados.calorias || 0)} kcal
                </p>
              </div>

              <div className="recap-modal-card">
                <h4><FontAwesomeIcon icon={faTint} /> Água</h4>
                <p className="recap-modal-valor-grande">
                  {((dadosSelecionados.agua || 0) / 1000).toFixed(2)} L
                </p>
              </div>

              <div className="recap-modal-card">
                <h4><FontAwesomeIcon icon={faWeightScale} /> Peso</h4>
                <p className="recap-modal-valor-grande">
                  {dadosSelecionados.peso ? `${dadosSelecionados.peso.toFixed(1)} kg` : 'Sem registro'}
                </p>
              </div>
            </div>

            <div className="recap-modal-macros">
              <h4>Macronutrientes</h4>
              <div className="recap-modal-macros-list">
                <div className="macro-item">
                  <FontAwesomeIcon icon={faEgg} />
                  <span>Proteínas</span>
                  <strong>{(dadosSelecionados.proteinas || 0).toFixed(1)} g</strong>
                </div>
                <div className="macro-item">
                  <FontAwesomeIcon icon={faBreadSlice} />
                  <span>Carboidratos</span>
                  <strong>{(dadosSelecionados.carboidratos || 0).toFixed(1)} g</strong>
                </div>
                <div className="macro-item">
                  <FontAwesomeIcon icon={faCheese} />
                  <span>Gorduras</span>
                  <strong>{(dadosSelecionados.gorduras || 0).toFixed(1)} g</strong>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default Recap;
